// Finding the list inside a response body.
//
// Endpoints wrap collections differently — sometimes the body is the array,
// sometimes it's under `data`, sometimes under a named key inside `data`
// alongside pagination counts. Rather than keep adding key names each time a
// list silently comes back empty, this checks the usual spots and then falls
// back to scanning for the first array it can find.

const NAMED_KEYS = [
  "items",
  "results",
  "rows",
  "records",
  "list",
  "conversations",
  "assistants",
  "messages",
  "plans",
  "integrations",
];

const firstArrayIn = (source) => {
  if (!source || typeof source !== "object") return null;

  for (const key of NAMED_KEYS) {
    if (Array.isArray(source[key])) return source[key];
  }

  // Last resort: any array value on the object.
  const found = Object.values(source).find(Array.isArray);
  return found || null;
};

/**
 * @param {object} resp an axios-style response
 * @returns {Array} the collection, or [] when there isn't one
 */
export const readList = (resp) => {
  const body = resp?.data;

  if (Array.isArray(body)) return body;
  if (Array.isArray(body?.data)) return body.data;

  return firstArrayIn(body?.data) || firstArrayIn(body) || [];
};

const numberOr = (value, fallback) => {
  const parsed = Number(value);
  return Number.isFinite(parsed) && parsed > 0 ? parsed : fallback;
};

/**
 * The counts that sit alongside a paged collection. Field names vary between
 * endpoints, and `totalPages` is derived when only a total and a limit are
 * sent - the pager needs a last page either way.
 *
 * @param {object} resp an axios-style response
 * @param {number} fallbackLimit the limit that was requested
 * @returns {{page: number, limit: number, total: number, totalPages: number}}
 */
export const readPagination = (resp, fallbackLimit = 10) => {
  // The counts sit beside the array, not inside it, and the array itself may
  // be one or two levels down - so walk the plausible wrappers and take the
  // first that actually carries counts.
  const wrappers = [resp?.data, resp?.data?.data].filter(
    (value) => value && typeof value === "object" && !Array.isArray(value),
  );

  const meta =
    wrappers.map((w) => w.pagination || w.meta).find(Boolean) ||
    wrappers.find((w) => w.page || w.currentPage || w.total || w.totalCount) ||
    {};

  const limit = numberOr(
    meta.limit ?? meta.perPage ?? meta.pageSize,
    fallbackLimit,
  );
  const page = numberOr(meta.page ?? meta.currentPage, 1);
  const total = numberOr(meta.total ?? meta.totalCount ?? meta.count, 0);
  const totalPages = numberOr(
    meta.totalPages ?? meta.pages ?? meta.lastPage,
    total ? Math.ceil(total / limit) : 1,
  );

  return { page, limit, total, totalPages };
};
